import {useMemo} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";
import {formatISO, parseISO, startOfMonth, sub} from "date-fns";

import {Container} from "@pg-design/grid-module";
import {Text} from "@pg-design/text-module";

import {IRPStore} from "../../../app/rp_reducer";
import {IRegionReportPrice} from "../../../region/types/IRegionReportPrice";
import {getTransactionPricesDateRange} from "../actions/fetch_transaction_prices";
import {useGetRcnDataQuery} from "../api/get_rcn_data_api";
import {PricesCityAnalyticsLocation} from "../tracking/prices_city_analytics";
import {TrendSeriesPoint} from "../types/TrendSeriesPoint";
import {getAvgPriceM2ForRooms} from "../utils/get_avg_price_m2_for_rooms";
import {getMedianPriceM2} from "../utils/get_median_price_m2";
import {getPricesAvailableDate} from "../utils/get_prices_available_date";
import {getRcnPriceStatsQuery, RCN_TREND_MONTHS} from "../utils/get_rcn_params";
import {getTrendInsight} from "../utils/get_trend_insight";
import {PricesTrendChart} from "./prices_trend/PricesTrendChart";
import {PricesTrendInsight} from "./prices_trend/PricesTrendInsight";
import {PricesTrendLegend} from "./prices_trend/PricesTrendLegend";
import {PricesCityViewSectionTracker} from "./PricesCityViewSectionTracker";
import {usePricesContext} from "./PricesContextFilterProvider";
import {PriceStatSwitch} from "./PriceStatSwitch";
import {RoomsFilterSelect} from "./RoomsFilterSelect";

interface Props {
    region: IRegionReportPrice;
    location: PricesCityAnalyticsLocation;
}

export const PricesTrend = (props: Props) => {
    const {region, location} = props;
    const {rooms, priceStat} = usePricesContext();

    const prices = useSelector((store: IRPStore) => store.report.price.prices);
    const pricesAvailableDate = useSelector((store: IRPStore) => store.report.price.pricesAvailableDate);
    const referenceDate = getPricesAvailableDate(pricesAvailableDate?.date);
    // Median has no per-room breakdown in the source data, so it comes back null for a month below the
    // sample threshold - such a month is dropped from the line rather than plotted as a fake zero.
    const rpSeries = useMemo(
        () =>
            prices
                ? prices
                      .map((record) => ({date: record.date, value: priceStat === "median" ? getMedianPriceM2(record) : getAvgPriceM2ForRooms(record, rooms)}))
                      .filter((point): point is TrendSeriesPoint => point.value !== null)
                : null,
        [prices, rooms, priceStat]
    );

    // Overrides the query's default fixed-quarter cutoff: shows the freshest window BigData/RCN has instead of
    // stopping at one settled quarter. date_to mirrors the same month the BigData query above resolves to,
    // so both queries stay capped at the same point.
    const {data: rcnResponse, isFetching: isRcnFetching} = useGetRcnDataQuery({
        ...getRcnPriceStatsQuery(region.id, referenceDate, rooms, RCN_TREND_MONTHS, "month", priceStat === "median" ? "median" : "trimmed_mean_5"),
        date_to: formatISO(sub(referenceDate, {months: 1}), {representation: "date"})
    });

    const rcnSeriesRaw = useMemo(
        () =>
            // reverse() sorts series for chart (oldest -> newest)
            rcnResponse ? [...rcnResponse.series].reverse().map((point) => ({date: point.period_start, value: point.value, count: point.n_rows})) : null,
        [rcnResponse]
    );

    // RCN's registry lag often leaves it short of BigData's range - the chart's range is BigData's (rpSeries
    // stays uncut); RCN gets clipped to fit inside it rather than stretching the axis to its own older span.
    const rcnSeries = useMemo(() => {
        if (!rcnSeriesRaw || !rpSeries) {
            return rcnSeriesRaw;
        }

        const rpFirstDate = rpSeries[0]?.date;
        const rpLastDate = rpSeries[rpSeries.length - 1]?.date;

        return rcnSeriesRaw.filter((point) => (!rpFirstDate || point.date >= rpFirstDate) && (!rpLastDate || point.date <= rpLastDate));
    }, [rcnSeriesRaw, rpSeries]);

    // Boundary between settled data and the months still filling in, so the chart can mark the latter dashed.
    // Must land on the quarter's first day, not date_to's last day: Highcharts zones style a segment by its
    // STARTING point, so anchoring on date_to would turn the dash on one point too late.
    const dashedFromDate = formatISO(startOfMonth(parseISO(getTransactionPricesDateRange(referenceDate).date_to)), {representation: "date"});

    const insight = useMemo(() => (prices ? getTrendInsight(prices, rooms) : null), [prices, rooms]);

    return (
        <div className={sectionWrapper}>
            <Container className={containerStyle}>
                <PricesCityViewSectionTracker sectionName="price_trend" location={location} />

                <div className={cardStyle}>
                    <Text variant="headline_3" as="h2" mb={1}>
                        Ceny mieszkań {region.short_name} wykres
                    </Text>

                    <div className={filtersWrapperStyle}>
                        <PriceStatSwitch location={location} />
                        <RoomsFilterSelect instanceId="prices-trend-rooms-select" sectionName="price_trend" location={location} />
                    </div>

                    <PricesTrendChart
                        rpSeries={rpSeries}
                        rcnSeries={rcnSeries ?? []}
                        dashedFromDate={dashedFromDate}
                        priceStat={priceStat}
                        isLoading={isRcnFetching}
                    />

                    <PricesTrendLegend hasRcnData={(rcnSeries ?? []).length > 0} />
                </div>

                {insight && <PricesTrendInsight insight={insight} region={region} rooms={rooms} />}
            </Container>
        </div>
    );
};

const sectionWrapper = css({
    w: "100%",
    py: 3,
    backgroundColor: "var(--colors-beige-subtle)",

    md: {
        py: 8
    }
});

const containerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,

    lg: {
        px: 10
    }
});

const filtersWrapperStyle = css({
    display: "flex",
    flexDirection: "column-reverse",
    gap: 1,

    xs: {
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "flex-end"
    }
});

const cardStyle = css({
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--colors-white)",
    gap: 1.5,
    borderRadius: 3,
    p: 2,

    md: {
        p: 4
    }
});
import {useMemo} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";
import {formatISO, parseISO, startOfMonth, sub} from "date-fns";

import {Container} from "@pg-design/grid-module";
import {Text} from "@pg-design/text-module";

import {IRPStore} from "../../../app/rp_reducer";
import {IRegionReportPrice} from "../../../region/types/IRegionReportPrice";
import {getTransactionPricesDateRange} from "../actions/fetch_transaction_prices";
import {useGetRcnDataQuery} from "../api/get_rcn_data_api";
import {PricesCityAnalyticsLocation} from "../tracking/prices_city_analytics";
import {TrendSeriesPoint} from "../types/TrendSeriesPoint";
import {getAvgPriceM2ForRooms} from "../utils/get_avg_price_m2_for_rooms";
import {getMedianPriceM2} from "../utils/get_median_price_m2";
import {getPricesAvailableDate} from "../utils/get_prices_available_date";
import {getRcnPriceStatsQuery, RCN_TREND_MONTHS} from "../utils/get_rcn_params";
import {getTrendInsight} from "../utils/get_trend_insight";
import {PricesTrendChart} from "./prices_trend/PricesTrendChart";
import {PricesTrendInsight} from "./prices_trend/PricesTrendInsight";
import {PricesTrendLegend} from "./prices_trend/PricesTrendLegend";
import {PricesCityViewSectionTracker} from "./PricesCityViewSectionTracker";
import {usePricesContext} from "./PricesContextFilterProvider";
import {PriceStatSwitch} from "./PriceStatSwitch";
import {RoomsFilterSelect} from "./RoomsFilterSelect";

interface Props {
    region: IRegionReportPrice;
    location: PricesCityAnalyticsLocation;
}

export const PricesTrend = (props: Props) => {
    const {region, location} = props;
    const {rooms, priceStat} = usePricesContext();

    const prices = useSelector((store: IRPStore) => store.report.price.prices);
    const pricesAvailableDate = useSelector((store: IRPStore) => store.report.price.pricesAvailableDate);
    const referenceDate = getPricesAvailableDate(pricesAvailableDate?.date);
    // Median has no per-room breakdown in the source data, so it comes back null for a month below the
    // sample threshold - such a month is dropped from the line rather than plotted as a fake zero.
    const rpSeries = useMemo(
        () =>
            prices
                ? prices
                      .map((record) => ({date: record.date, value: priceStat === "median" ? getMedianPriceM2(record) : getAvgPriceM2ForRooms(record, rooms)}))
                      .filter((point): point is TrendSeriesPoint => point.value !== null)
                : null,
        [prices, rooms, priceStat]
    );

    // Overrides the query's default fixed-quarter cutoff: shows the freshest window BigData/RCN has instead of
    // stopping at one settled quarter. date_to mirrors the same month the BigData query above resolves to,
    // so both queries stay capped at the same point.
    const {data: rcnResponse, isFetching: isRcnFetching} = useGetRcnDataQuery({
        ...getRcnPriceStatsQuery(region.id, referenceDate, rooms, RCN_TREND_MONTHS, "month", priceStat === "median" ? "median" : "trimmed_mean_5"),
        date_to: formatISO(sub(referenceDate, {months: 1}), {representation: "date"})
    });

    const rcnSeriesRaw = useMemo(
        () =>
            // reverse() sorts series for chart (oldest -> newest)
            rcnResponse ? [...rcnResponse.series].reverse().map((point) => ({date: point.period_start, value: point.value, count: point.n_rows})) : null,
        [rcnResponse]
    );

    // RCN's registry lag often leaves it short of BigData's range - the chart's range is BigData's (rpSeries
    // stays uncut); RCN gets clipped to fit inside it rather than stretching the axis to its own older span.
    const rcnSeries = useMemo(() => {
        if (!rcnSeriesRaw || !rpSeries) {
            return rcnSeriesRaw;
        }

        const rpFirstDate = rpSeries[0]?.date;
        const rpLastDate = rpSeries[rpSeries.length - 1]?.date;

        return rcnSeriesRaw.filter((point) => (!rpFirstDate || point.date >= rpFirstDate) && (!rpLastDate || point.date <= rpLastDate));
    }, [rcnSeriesRaw, rpSeries]);

    // Boundary between settled data and the months still filling in, so the chart can mark the latter dashed.
    // Must land on the quarter's first day, not date_to's last day: Highcharts zones style a segment by its
    // STARTING point, so anchoring on date_to would turn the dash on one point too late.
    const dashedFromDate = formatISO(startOfMonth(parseISO(getTransactionPricesDateRange(referenceDate).date_to)), {representation: "date"});

    const insight = useMemo(() => (prices ? getTrendInsight(prices, rooms) : null), [prices, rooms]);

    return (
        <div className={sectionWrapper}>
            <Container className={containerStyle}>
                <PricesCityViewSectionTracker sectionName="price_trend" location={location} />

                <div className={cardStyle}>
                    <Text variant="headline_3" as="h2" mb={1}>
                        Ceny mieszkań {region.short_name} wykres
                    </Text>

                    <div className={filtersWrapperStyle}>
                        <PriceStatSwitch location={location} />
                        <RoomsFilterSelect instanceId="prices-trend-rooms-select" sectionName="price_trend" location={location} />
                    </div>

                    <PricesTrendChart
                        rpSeries={rpSeries}
                        rcnSeries={rcnSeries ?? []}
                        dashedFromDate={dashedFromDate}
                        priceStat={priceStat}
                        isLoading={isRcnFetching}
                    />

                    <PricesTrendLegend hasRcnData={(rcnSeries ?? []).length > 0} />
                </div>

                {insight && <PricesTrendInsight insight={insight} region={region} rooms={rooms} />}
            </Container>
        </div>
    );
};

const sectionWrapper = css({
    w: "100%",
    py: 3,
    backgroundColor: "var(--colors-beige-subtle)",

    md: {
        py: 8
    }
});

const containerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,

    lg: {
        px: 10
    }
});

const filtersWrapperStyle = css({
    display: "flex",
    flexDirection: "column-reverse",
    gap: 1,

    xs: {
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "flex-end"
    }
});

const cardStyle = css({
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--colors-white)",
    gap: 1.5,
    borderRadius: 3,
    p: 2,

    md: {
        p: 4
    }
});
